import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePageWithLoadingOverlay } from '@/components/PageWithLoadingOverlay';
import PageNumberSection from '@/components/pools/list/filters/PageNumberSection';
import { ContentWithLoading } from '@/components/ui/ContentWithLoading';
import { usePoolsPagination } from '@/hooks/usePoolsPagination';
import { PoolFilters } from '@/types/pool';
import PoolFiltersDropdown from './filters/PoolFiltersDropdown';
import PoolCard from './PoolCard';
import { PoolListHeader } from './PoolListHeader';

export type PoolListTab = { id: string; label: string };

export type PoolListEmptyMessages = Record<string, { title: string; subtitle: string }>;

type ReadyPoolListProps = {
  tabs: PoolListTab[];
  defaultTab: string;
  getTabFilters: (tabId: string) => PoolFilters;
  emptyMessages: PoolListEmptyMessages;
  onShowDraftPools: () => void;
};

export function ReadyPoolList({
  tabs,
  defaultTab,
  getTabFilters,
  emptyMessages,
  onShowDraftPools,
}: ReadyPoolListProps) {
  const [activeTab, setActiveTab] = useState<string>(defaultTab);
  const [showFilters, setShowFilters] = useState(false);
  const [dropdownFilters, setDropdownFilters] = useState<PoolFilters>({});

  const filters = useMemo<PoolFilters>(() => {
    return { ...getTabFilters(activeTab), ...dropdownFilters };
  }, [activeTab, dropdownFilters, getTabFilters]);

  const { pagination, handlePageChange, pools, isLoading, isFetching } = usePoolsPagination(filters);
  const { showLoadingOverlay, hideLoadingOverlay } = usePageWithLoadingOverlay();

  useEffect(() => {
    if (isFetching) {
      showLoadingOverlay();
    } else {
      hideLoadingOverlay();
    }
  }, [isFetching, showLoadingOverlay, hideLoadingOverlay]);

  const handleTabChange = useCallback(
    (tabId: string) => {
      setActiveTab(tabId);
      handlePageChange(1);
    },
    [handlePageChange]
  );

  const handleFiltersChange = useCallback(
    (newFilters: PoolFilters) => {
      setDropdownFilters(newFilters);
      handlePageChange(1);
    },
    [handlePageChange]
  );

  const hasFilters = dropdownFilters && Object.keys(dropdownFilters).length > 0;

  const emptyTitle = useMemo(() => {
    if (isFetching) return 'Loading...';
    if (hasFilters) return 'No pools found with your filters';
    return emptyMessages[activeTab]?.title ?? 'No pools';
  }, [isFetching, hasFilters, emptyMessages, activeTab]);

  const emptySubtitle = useMemo(() => {
    if (isFetching) return '';
    if (hasFilters)
      return 'There are no results that match your selected criteria. You can modify your filters or reset them';
    return emptyMessages[activeTab]?.subtitle ?? '';
  }, [isFetching, hasFilters, emptyMessages, activeTab]);

  return (
    <div className="w-full">
      <PoolListHeader
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onShowFilters={() => setShowFilters(!showFilters)}
        onShowDraftPools={onShowDraftPools}
        showDraftPools={false}
      />
      {showFilters && <PoolFiltersDropdown onFiltersChange={handleFiltersChange} />}
      <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        <ContentWithLoading
          loading={isLoading}
          empty={!isLoading && pools.length === 0}
          emptyTitle={emptyTitle}
          emptySubtitle={emptySubtitle}
          className="col-span-full font-avenir"
        >
          {pools.map((pool) => (
            <PoolCard key={pool.id} pool={pool} />
          ))}
        </ContentWithLoading>
      </div>
      <PageNumberSection
        currentPage={pagination.page}
        hasNext={pagination.has_next}
        hasPrevious={pagination.has_previous}
        totalPages={pagination.total_pages}
        handlePageChange={handlePageChange}
      />
    </div>
  );
}
